import { Home, MoreHorizontal, Plus, EyeOff, Copy, Trash2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import SortableList from "./SortableList";
import { useEditor, pagePath } from "./useEditorState";

export function PanelHeader({ title, subtitle, action }) {
  return (
    <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{title}</p>
        {subtitle && <p className="text-sm font-semibold text-slate-900 truncate mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function PagesPanel() {
  const ed = useEditor();
  const { content, pageId, goToPage, setSelection, selection } = ed;
  const add = () => { const id = ed.addPage("Nuova pagina"); setTimeout(() => goToPage(id), 50); setSelection({ kind: "page", id }); ed.setUi({ rightTab: "props" }); };
  const del = (p) => {
    if (content.pages.length <= 1) return toast.error("Il progetto deve avere almeno una pagina");
    ed.setUi({ confirm: { title: `Eliminare la pagina "${p.name}"?`, description: "Verranno eliminate anche tutte le sue sezioni.",
      onConfirm: () => { ed.deletePage(p.id); if (pageId === p.id) goToPage(content.pages.find((x) => x.id !== p.id).id); } } });
    return null;
  };
  return (
    <div data-testid="pages-panel">
      <PanelHeader title="Pagine" subtitle={`${content.pages.length} pagine`} action={<Button size="sm" variant="outline" className="h-8" onClick={add} data-testid="add-page-btn"><Plus size={14} className="mr-1" />Pagina</Button>} />
      <div className="px-2">
        <SortableList items={content.pages} onMove={ed.movePage} testIdPrefix="page-row" render={(p) => {
          const active = p.id === pageId;
          return (
            <div className={`group flex items-center gap-1 rounded-md pr-1 ${active ? "bg-sky-50" : "hover:bg-slate-100"} ${selection?.id === p.id ? "ring-1 ring-sky-400" : ""}`}>
              <button className="flex-1 min-w-0 text-left px-2 py-2" onClick={() => { goToPage(p.id); setSelection({ kind: "page", id: p.id }); ed.setUi({ rightTab: "props", rightOpen: true }); }} data-testid={`page-item-${p.slug}`}>
                <p className={`text-sm truncate flex items-center gap-1.5 ${active ? "text-sky-800 font-medium" : "text-slate-800"}`} title={p.name}>
                  {p.id === content.home_page_id && <Home size={13} className="shrink-0" />}{p.name}
                  {!p.in_nav && <EyeOff size={12} className="text-slate-400 shrink-0" aria-label="Non nel menu" />}
                </p>
                <p className="text-[11px] text-slate-500 font-mono truncate">{pagePath(content, p)} · {p.sections.length} sez.</p>
              </button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="p-1.5 rounded text-slate-400 hover:text-slate-800 opacity-70 group-hover:opacity-100" data-testid={`page-menu-${p.slug}`}><MoreHorizontal size={15} /></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setSelection({ kind: "page", id: p.id }); ed.setUi({ rightTab: "props", rightOpen: true }); }}><Settings2 size={14} className="mr-2" />Impostazioni pagina e SEO</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => ed.setHome(p.id)} disabled={p.id === content.home_page_id} data-testid={`page-sethome-${p.slug}`}><Home size={14} className="mr-2" />Imposta come homepage</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => ed.updatePage(p.id, { in_nav: !p.in_nav })}><EyeOff size={14} className="mr-2" />{p.in_nav ? "Rimuovi dal menu" : "Mostra nel menu"}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { const id = ed.duplicatePage(p.id); toast.success("Pagina duplicata"); setTimeout(() => goToPage(id), 50); }} data-testid={`page-duplicate-${p.slug}`}><Copy size={14} className="mr-2" />Duplica</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600" onClick={() => del(p)} data-testid={`page-delete-${p.slug}`}><Trash2 size={14} className="mr-2" />Elimina</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        }} />
      </div>
      <p className="px-4 py-4 text-[11px] text-slate-500 leading-relaxed">L'ordine delle pagine definisce il menu di navigazione. Trascina per riordinare.</p>
    </div>
  );
}
